import { z } from 'zod';

export const reviewStatusSchema = z.enum(['draft', 'in-progress', 'pending-approval', 'completed', 'archived']);
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;

export const reviewPeriodSchema = z.enum(['quarterly', 'semi-annual', 'annual']);
export type ReviewPeriod = z.infer<typeof reviewPeriodSchema>;

export const reviewSchema = z.object({
  reviewId: z.string(),
  employeeId: z.string(),
  employeeName: z.string(),
  employeeRole: z.string(),
  reviewerId: z.string(),
  reviewerName: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  period: reviewPeriodSchema,
  periodStart: z.string(),
  periodEnd: z.string(),
  status: reviewStatusSchema,
  overallRating: z.number().min(1).max(5).optional(),
  strengths: z.string().optional(),
  areasForImprovement: z.string().optional(),
  achievements: z.string().optional(),
  developmentPlan: z.string().optional(),
  employeeComments: z.string().optional(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  dueDate: z.string(),
});

export type Review = z.infer<typeof reviewSchema>;

export const reviewListResponseSchema = z.object({
  data: z.array(reviewSchema),
  pagination: z.object({
    nextToken: z.string().nullable(),
    totalCount: z.number(),
  }),
});

export type ReviewListResponse = z.infer<typeof reviewListResponseSchema>;

export interface ReviewFilters {
  employeeId?: string;
  reviewerId?: string;
  status?: ReviewStatus;
  period?: ReviewPeriod;
  siteId?: string;
  search?: string;
}

export function getReviewStatusColor(status: ReviewStatus): string {
  const colors: Record<ReviewStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    'in-progress': 'bg-blue-100 text-blue-700',
    'pending-approval': 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-500',
  };
  return colors[status];
}

export function getReviewPeriodLabel(period: ReviewPeriod): string {
  const labels: Record<ReviewPeriod, string> = {
    quarterly: 'Quarterly',
    'semi-annual': 'Semi-Annual',
    annual: 'Annual',
  };
  return labels[period];
}
